import { ReactNode, createContext, useContext, useState } from "react";

import { Ds } from "@accurx/design";
import {
    getPrincipalSystemFriendlyName,
    useBrowserEnvironment,
    useMedicalRecordConnection,
} from "@accurx/native";
import { PatientSearchResult } from "@accurx/patient";
import { getRouteFromComposeVariant } from "domains/compose/utils/getRouteFromComposeVariant";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { mapDemographicsToCurrentPatient } from "../../../utils/mapDemographicsToCurrentPatient";
import { mapRouterStateToCurrentPatient } from "../../../utils/mapRouterStateToCurrentPatient";
import { AppOriginType, ComposeVariant, RouterState } from "../../Routes.types";
import { MedicalRecordPatientUpdater } from "../components/MedicalRecordPatientUpdater/MedicalRecordPatientUpdater";
import { StyledSpinnerWrapper } from "./CurrentPatientProvider.styles";
import { CurrentPatient } from "./CurrentPatientProvider.types";
import { useMedicalRecordFeedPatient } from "./NativePatientFeedProvider";
import { useCurrentPatientDelay } from "./useCurrentPatientDelay";

type CurrentPatientContextType = {
    currentPatient: CurrentPatient | null;
    isPatientStale: boolean;
    handleSelectPatient: (args: {
        workspaceId: number;
        result: PatientSearchResult | null;
        history: ReturnType<typeof useHistory>;
        composeVariant: ComposeVariant;
        appOrigin?: AppOriginType;
    }) => void;
};

const CurrentPatientContext = createContext<CurrentPatientContextType | null>(
    null,
);

export const LocationStatePatientProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { state } = useLocation<RouterState>();
    const currentPatientFromState = mapRouterStateToCurrentPatient(state);

    return (
        <CurrentPatientContext.Provider
            value={{
                currentPatient: currentPatientFromState,
                isPatientStale: false,
                handleSelectPatient: () => {
                    throw new Error(
                        "Can't do a patient search in this context",
                    );
                },
            }}
        >
            {children}
        </CurrentPatientContext.Provider>
    );
};

/**
 * This provider is in charge of managing the patient
 * that should be displayed on the Compose page
 */
export const CurrentPatientProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { medicalRecordPatient } = useMedicalRecordFeedPatient();
    const medicalRecordConnection = useMedicalRecordConnection();

    const location = useLocation<RouterState>();
    const history = useHistory();

    const browserEnv = useBrowserEnvironment();
    const [currentPatient, setCurrentPatient] = useState<CurrentPatient | null>(
        medicalRecordPatient
            ? mapDemographicsToCurrentPatient(medicalRecordPatient)
            : mapRouterStateToCurrentPatient(location.state),
    );
    const [isPatientStale, setIsPatientStale] = useState(false);

    /**
     * Clear the searched patient from the location state if it exists.
     *
     * It's possible to end up in an infinate loop if we don't clear the
     * patient from location state. This occurs when we clear the patient,
     * it is then set again as the user navigates between compose pages
     * in the background while the window is closed.
     *
     * We should consider refactoring so that the location state patient
     * is cleared as soon as the value is loaded into state.
     */
    const clearSearchedPatientFromLocationState = () => {
        if (location.state?.searchedPatient) {
            history.replace({
                ...location,
                state: {
                    ...location.state,
                    searchedPatient: undefined,
                },
            });
        }
    };

    const currentPatientDelay = useCurrentPatientDelay(!!medicalRecordPatient);

    return (
        <CurrentPatientContext.Provider
            value={{
                currentPatient,
                isPatientStale,
                handleSelectPatient: ({
                    result,
                    composeVariant,
                    workspaceId,
                    appOrigin,
                }) => {
                    if (result) {
                        setCurrentPatient(
                            mapRouterStateToCurrentPatient({
                                searchedPatient: result,
                            }),
                        );

                        const route =
                            getRouteFromComposeVariant(composeVariant);

                        history.push(
                            generatePath(route, {
                                workspaceId,
                            }),
                            { searchedPatient: result, appOrigin },
                        );
                    }
                },
            }}
        >
            {browserEnv === "WebView"
                ? medicalRecordConnection.status !== null && (
                      <MedicalRecordPatientUpdater
                          setIsPatientStale={setIsPatientStale}
                          setCurrentPatient={(patient) => {
                              clearSearchedPatientFromLocationState();
                              setCurrentPatient(patient);
                          }}
                          medicalRecordSystemFriendlyName={getPrincipalSystemFriendlyName(
                              medicalRecordConnection.system,
                          )}
                          currentPatientDelay={currentPatientDelay}
                      />
                  )
                : null}
            {currentPatientDelay ? (
                <StyledSpinnerWrapper>
                    <Ds.Spinner />
                </StyledSpinnerWrapper>
            ) : (
                <>{children}</>
            )}
        </CurrentPatientContext.Provider>
    );
};

export const useCurrentPatient = () => {
    const context = useContext(CurrentPatientContext);

    if (context === null) {
        throw new Error(
            "useCurrentPatient can only be used by a child of a CurrentPatientProvider.",
        );
    }

    return context;
};
